import { type ReactNode, useId, useMemo, useState } from 'react';
import styles from './Field.module.css';
import { FieldContext, type FieldContextValue } from './fieldContext';

interface FieldBaseProps {
    /** Sentence case, per §9 — "Buying standard", not "Buying Standard". Visible unless `hideLabel`. */
    readonly label: string;
    /**
     * Keeps the label for a screen reader but does not draw it. Only for a control whose purpose the screen
     * already states in the same words just before it is shown: the note composer, which opens from an *Add a
     * note* line, so an *Add a note* label over the box would say it twice. Every other field shows its label.
     */
    readonly hideLabel?: boolean;
    /**
     * Rendered inside the `<label>` (or `<legend>`) after the label text and the required `*`, with a
     * space before it, so it becomes part of the control's accessible name — F12's per-field
     * AI-drafted marker (`AiFieldMarker`, design-system §5 *AI provenance*). Nothing interactive:
     * a label's content is its control's name, and a control inside a label is a second target.
     */
    readonly labelAddon?: ReactNode;
    /** Marks the label `Buying standard *` and sets `required` on the control. */
    readonly required?: boolean;
    /** Format guidance under the control. Replaced by the error message while there is one (§5). */
    readonly helper?: string;
    /** Fixes the generated id, for a caller that needs to reference the control from outside. */
    readonly id?: string;
    /**
     * `control` (the default) labels ONE labelable control with `<label for>`.
     * `group` labels a SET — a row of radios, a pair of date inputs — as a `role="group"`, because
     * `<label for>` can only point at one element and pointing it at the first radio silently
     * mislabels the rest.
     */
    readonly labels?: 'control' | 'group';
    readonly children: ReactNode;
    /** `| undefined` explicitly — see the same note on `Input`'s test hook. */
    readonly 'data-testid'?: string | undefined;
}

interface ControlledFieldProps extends FieldBaseProps {
    /**
     * The caller owns validation — a server response, a cross-field rule.
     *
     * `| undefined` explicitly, because `exactOptionalPropertyTypes` is on and the usual caller
     * computes the message: `error={maybeError}` is passing undefined, not omitting the prop.
     */
    readonly error?: string | undefined;
    readonly validate?: never;
}

interface ValidatedFieldProps extends FieldBaseProps {
    /**
     * The field owns §5's timing: run on blur, then re-run on every change WHILE invalid so a
     * correction clears the message as soon as it is fixed. Return `undefined` when the value is
     * good, otherwise the message — which names the FIX, not the failure ("Enter a date on or after
     * today", not "Invalid date").
     *
     * The value is the control's `value` for text, select and date controls, and `'true'` / `''`
     * for the checkbox-shaped ones, so a required-checkbox rule reads
     * `(v) => (v ? undefined : 'Accept the terms to continue')`.
     */
    readonly validate: (value: string) => string | undefined;
    readonly error?: never;
}

/**
 * The two are mutually exclusive by construction. A field that both took an `error` prop and ran
 * its own validator would have two sources for one message, and which one won would be a detail of
 * this file rather than something the call site states.
 */
export type FieldProps = ControlledFieldProps | ValidatedFieldProps;

/** Empty and absent are the same thing here — an `error=""` is not an error. */
function textOrUndefined(value: string | undefined): string | undefined {
    return value === undefined || value.trim().length === 0 ? undefined : value;
}

/**
 * §5's Form fields row, whole: **label + control + optional helper + reserved error slot**.
 *
 * The reserved slot is the part worth stating plainly, because it looks like padding and is not.
 * The message paragraph is rendered ALWAYS — empty when there is nothing to say — and reserves one
 * line of `type/meta`. Rendering it conditionally would push everything below it down the page the
 * moment a validation message appeared, which on a long form means the button the user is reaching
 * for moves out from under the pointer. FS-3 AC-16 measures the wrapper's height in both states for
 * exactly this reason.
 *
 * Helper text and the error share ONE node rather than stacking, per §5's "helper text ... is
 * replaced by the error when there is one". That is also what keeps the reservation honest: two
 * nodes would reserve two lines, or shift by one.
 *
 * **On submit — documented here, not built here (FS-3 FR-C5).** A form that submits with invalid fields
 * owes the user two things this component cannot provide alone, because both are properties of the
 * FORM rather than of one field: focus moves to the first invalid control, and a summary at the top
 * lists every error as a link to its field. Both are reachable from what this component already
 * exposes — the control carries a stable `id` (pass `id` to fix it) and the message node is
 * `<id>-message` — so the first real form wires them without changing this file. They arrive with
 * that form rather than now, because a summary with nothing to summarise cannot be verified against
 * a real submit.
 */
export function Field(props: FieldProps) {
    const {
        label,
        required = false,
        helper,
        labels = 'control',
        labelAddon,
        hideLabel = false,
        children,
        'data-testid': testId,
        validate,
    } = props;
    const labelClass = hideLabel ? `${styles.label} ${styles.labelHidden}` : styles.label;

    const generatedId = useId();
    const fieldId = props.id ?? generatedId;
    const controlId = `${fieldId}-control`;
    const labelId = `${fieldId}-label`;
    const messageSlotId = `${fieldId}-message`;

    const [selfError, setSelfError] = useState<string | undefined>(undefined);

    const error = textOrUndefined(props.error) ?? textOrUndefined(selfError);
    const message = error ?? textOrUndefined(helper);
    // Absent while the slot is empty: a reference to an empty element reads as handled and
    // announces nothing, which is worse than no reference at all.
    const messageDescribedBy = message === undefined ? undefined : messageSlotId;

    const contextValue = useMemo<FieldContextValue>(
        () => ({
            // Group mode offers no control id — see the note on the context type for why handing
            // one id to a whole set of radios is a document-validity bug, not a nicety.
            controlId: labels === 'control' ? controlId : undefined,
            // Only while the slot has something to read — see the note on the context type.
            messageId: messageDescribedBy,
            invalid: error !== undefined,
            required,
            reportBlur: (value: string) => {
                if (validate === undefined) {
                    return;
                }
                setSelfError(validate(value));
            },
            reportChange: (value: string) => {
                if (validate === undefined) {
                    return;
                }
                // §5: "validate on blur, never per keystroke; once a field is invalid, re-validate
                // as it is corrected". The updater form is what encodes the second half — a field
                // that has said nothing yet stays quiet, and one that is already complaining
                // re-checks on every keystroke so the message clears the instant it is fixed.
                setSelfError((current) => (current === undefined ? current : validate(value)));
            },
        }),
        [controlId, labels, messageDescribedBy, error, required, validate],
    );

    const labelText = (
        <>
            {label}
            {/* Decorative: `required` on the control is what a screen reader announces, so reading
                the asterisk too would say it twice. §5 asks for the convention to be stated once
                per form, which is the form's job rather than this component's. */}
            {required ? <span aria-hidden="true"> *</span> : null}
            {/* The space keeps the addon's text a separate word in the accessible name. */}
            {labelAddon === undefined || labelAddon === null ? null : <> {labelAddon}</>}
        </>
    );

    /*
     * The rows below the label, shared by both modes — which is what keeps the wrapper's element
     * structure identical whichever one is in use, and so keeps one height measurement good for
     * both (FS-3 AC-16).
     */
    const rows = (
        <>
            <div className={styles.control}>{children}</div>

            {/* ALWAYS present — see the reserved-slot note in the component doc. `aria-live` rather
                than `role="alert"`, because this node also holds ordinary helper text: an alert
                role that came and went with the error would change the node's semantics under the
                reader mid-session. */}
            <p
                aria-live="polite"
                className={`${styles.message} ${error === undefined ? '' : styles.error}`}
                id={messageSlotId}
            >
                {message}
            </p>
        </>
    );

    /*
     * The two modes render as two different ELEMENTS rather than as one element with a conditional
     * `role`, and that is more than tidiness. A group is `<fieldset>` + `<legend>` — the native
     * grouping element, per FS-3 FR-C2's native-first posture — which supplies the group role and its
     * accessible name without an `aria-labelledby` to keep in step with an id. A conditional role
     * on a shared `<div>` would also be a lie to every static reader (a linter, a reviewer, the next
     * person) about what the element is.
     *
     * In group mode the message belongs to the GROUP: no single control owns a pair error like "the
     * end date is before the start date", and attaching it to one of them makes the other look
     * fine. Controls sitting DIRECTLY in the group (a row of radios) also take the reference from
     * context, so it is announced twice over — on entering the set, and from whichever option has
     * focus. A control nested in its own inner `Field` reads that inner context instead and must be
     * handed the group's message id explicitly; the `<id>-message` shape is stable for exactly that
     * (see `DateRangeInput`).
     */
    if (labels === 'group') {
        return (
            <FieldContext.Provider value={contextValue}>
                <fieldset
                    aria-describedby={messageDescribedBy}
                    className={styles.fieldset}
                    data-testid={testId}
                >
                    <legend className={labelClass} id={labelId}>
                        {labelText}
                    </legend>
                    {rows}
                </fieldset>
            </FieldContext.Provider>
        );
    }

    return (
        <FieldContext.Provider value={contextValue}>
            <div className={styles.field} data-testid={testId}>
                <label className={labelClass} htmlFor={controlId} id={labelId}>
                    {labelText}
                </label>
                {rows}
            </div>
        </FieldContext.Provider>
    );
}
